import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import type { SurveyForIntakeDto, SubmitSurveyRequestDto } from '../models/intake-dto';
import { IntakeService } from '../services/intake.service';

@Component({
  selector: 'app-form',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './form.component.html',
  styleUrl: './form.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormComponent implements OnInit, OnDestroy {
  @Input() survey: SurveyForIntakeDto | null = null;
  @Input() token: string = '';
  @Input() runId: number = 0;
  @Input() isPreview = false;
  /** When true, parent is handling submit (disable button). */
  @Input() submitInProgress = false;
  /** Optional error message from parent (e.g. submit failed). */
  @Input() submitError: string | null = null;

  @Output() readonly submitted = new EventEmitter<SubmitSurveyRequestDto>();

  errorMessage: string | null = null;
  responses: Map<number, number | string> = new Map(); // questionId -> answerId or freeFormText
  /** True when embedded in an iframe (sticky top bar won't stay visible; use flash workaround). */
  inIframe = false;
  /** Question id whose progress flash is currently visible (iframe workaround after answering). */
  progressFlashQuestionId: number | null = null;
  private readonly destroy$ = new Subject<void>();
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private progressFlashTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly PROGRESS_FLASH_MS = 2200;

  constructor(
    private readonly intake: IntakeService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.inIframe = this.detectIframe();
    this.cdr.markForCheck();

    if (!this.survey || this.isPreview || !this.runId || !this.token) return;
    const expires = new Date(this.survey.expiresAtUtc).getTime();
    const refreshAt = expires - 5 * 60 * 1000;
    const now = Date.now();
    if (refreshAt > now) {
      this.refreshTimer = setTimeout(() => this.refreshToken(), refreshAt - now);
    }
  }

  ngOnDestroy(): void {
    if (this.refreshTimer != null) clearTimeout(this.refreshTimer);
    if (this.progressFlashTimer != null) clearTimeout(this.progressFlashTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private detectIframe(): boolean {
    try {
      return typeof window !== 'undefined' && window.self !== window.top;
    } catch {
      return true;
    }
  }

  getQuestionOrdered(): Array<SurveyForIntakeDto['questions'][0]> {
    if (!this.survey?.questions?.length) return [];
    return [...this.survey.questions].sort((a, b) => a.displayOrder - b.displayOrder);
  }

  getAnswersOrdered(question: SurveyForIntakeDto['questions'][0]): Array<{ id: number; answerText: string }> {
    if (!question.answers?.length) return [];
    return [...question.answers].sort((a, b) => a.displayOrder - b.displayOrder);
  }

  maxChars(question: SurveyForIntakeDto['questions'][0]): number {
    return question.maxCharacterCount ?? 500;
  }

  setRadio(questionId: number, answerId: number): void {
    this.responses.set(questionId, answerId);
    this.showProgressFlash(questionId);
    this.cdr.markForCheck();
  }

  setFreeForm(questionId: number, value: string): void {
    const wasAnswered = (this.getFreeFormValue(questionId) || '').trim().length > 0;
    this.responses.set(questionId, value);
    const nowAnswered = (value || '').trim().length > 0;
    if (nowAnswered && !wasAnswered) {
      this.showProgressFlash(questionId);
    }
    this.cdr.markForCheck();
  }

  /** True while the brief post-answer progress feedback is showing for this question. */
  isProgressFlashVisible(questionId: number): boolean {
    return this.progressFlashQuestionId === questionId;
  }

  private showProgressFlash(questionId: number): void {
    if (this.isPreview || !this.inIframe) return;
    this.progressFlashQuestionId = questionId;
    if (this.progressFlashTimer != null) clearTimeout(this.progressFlashTimer);
    this.progressFlashTimer = setTimeout(() => {
      this.progressFlashTimer = null;
      this.progressFlashQuestionId = null;
      this.cdr.markForCheck();
    }, FormComponent.PROGRESS_FLASH_MS);
  }

  /** Number of questions that have at least one response (for progress bar). */
  get progressAnsweredCount(): number {
    const questions = this.getQuestionOrdered();
    return questions.filter((q) => this.isQuestionAnswered(q)).length;
  }

  get progressTotalCount(): number {
    return this.getQuestionOrdered().length;
  }

  /** Progress from 0 to 1 (or 0 to 100 for percentage). */
  get progressFraction(): number {
    const total = this.progressTotalCount;
    if (total === 0) return 1;
    return Math.min(1, this.progressAnsweredCount / total);
  }

  get progressPercent(): number {
    return Math.round(this.progressFraction * 100);
  }

  /**
   * Encouragement at milestone progress (scaled for any survey length;
   * for 10 questions: 5 → Halfway, 8 → Almost, 10 → You did it).
   */
  get progressFlavorText(): string | null {
    const answered = this.progressAnsweredCount;
    const total = this.progressTotalCount;
    if (total === 0 || answered === 0) return null;
    if (answered === total) return 'You did it!';
    if (answered === Math.round(total * 0.8)) return 'Almost at results!';
    if (answered === Math.round(total * 0.5)) return 'Halfway point!';
    return null;
  }

  private isQuestionAnswered(question: SurveyForIntakeDto['questions'][0]): boolean {
    if (question.questionType === 'Radio') {
      return this.getRadioValue(question.id) !== null;
    }
    return (this.getFreeFormValue(question.id) || '').trim().length > 0;
  }

  getRadioValue(questionId: number): number | null {
    const v = this.responses.get(questionId);
    return typeof v === 'number' ? v : null;
  }

  getFreeFormValue(questionId: number): string {
    const v = this.responses.get(questionId);
    return typeof v === 'string' ? v : '';
  }

  charCount(questionId: number): number {
    return (this.getFreeFormValue(questionId) || '').length;
  }

  refreshToken(): void {
    if (!this.runId || !this.token || this.isPreview) return;
    this.intake
      .refreshToken(this.runId, this.token)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (r) => {
          this.token = r.token;
          if (this.survey) {
            this.survey = { ...this.survey, token: r.token, expiresAtUtc: r.expiresAtUtc };
          }
        }
      });
  }

  submit(): void {
    if (!this.survey || !this.token || this.submitInProgress || this.isPreview) return;
    const items = this.buildResponses();
    if (items.length === 0) {
      this.errorMessage = 'Please answer at least one question.';
      return;
    }
    this.errorMessage = null;
    this.submitError = null;
    // Contact is collected on the results page after submission, not during survey fill-out.
    const req: SubmitSurveyRequestDto = { token: this.token, responses: items };
    this.submitted.emit(req);
  }

  private buildResponses(): SubmitSurveyRequestDto['responses'] {
    const out: SubmitSurveyRequestDto['responses'] = [];
    this.responses.forEach((value, surveyQuestionId) => {
      if (typeof value === 'number') {
        out.push({ surveyQuestionId, surveyAnswerId: value });
      } else {
        out.push({ surveyQuestionId, freeFormText: value || undefined });
      }
    });
    return out;
  }
}
